<script lang="ts">
  import { CHAT } from '../../game/constants/hud';
  import { UNNAMED_NAME } from '../../game/constants/world';
  import { isSystemChat, type ChatLine } from '../../game/world';

  interface Props {
    /** Newest first, already capped by `HudState`. */
    lines: readonly ChatLine[];
    uiWidth: number;
    uiHeight: number;
  }

  const { lines, uiWidth, uiHeight }: Props = $props();

  const oldestFirst = $derived([...lines].reverse());
  const width = $derived(uiHeight * CHAT.widthHeightFraction);
  const left = $derived(uiWidth * CHAT.startXFraction);
  const bottom = $derived(uiHeight * CHAT.startYFractionFromBottom);
  const maxHeight = $derived(uiHeight * CHAT.maxHeightFraction);
</script>

<!--
  Received chat, bottom left. The composer is `ChatComposer.svelte`, and only competitive worlds
  draw one: casual sets `WorldSetup.chat_enabled` false, exactly as the original's `chatEnabled`
  did, and the server declines C->S 29 there.

  The original drew one clipped row per line in a column a quarter of the viewport height wide.
  Measured at 20px xirod that column holds ten characters, and condensing 120 of them into it —
  which is what its `fillText` maxWidth did — lands at 9% horizontal scale, so it wraps here
  instead. Nothing was ever drawn in this area in the original (`chatEnabled` was always false),
  so there is no rendering to be faithful to.
-->
{#if lines.length > 0}
  <ol
    class="chat"
    style:left="{left}px"
    style:bottom="{bottom}px"
    style:width="{width}px"
    style:max-height="{maxHeight}px"
    style:font-size="{CHAT.font}px"
  >
    {#each oldestFirst as line, index (index)}
      <li class="chat__line" style:color={CHAT.teamColors[line.team] ?? CHAT.teamColors[2]}>
        {#if !isSystemChat(line)}
          <span class="chat__sender">{line.sender === '' ? UNNAMED_NAME : line.sender}</span>
        {/if}
        {line.text}
      </li>
    {/each}
  </ol>
{/if}

<style>
  .chat {
    position: absolute;
    display: flex;
    flex-direction: column;
    /* Newest pinned to the bottom, so an overlong line clips off the top rather than the tail. */
    justify-content: flex-end;
    gap: 2px;
    margin: 0;
    padding: 0;
    overflow: hidden;
    list-style: none;
  }

  .chat__line {
    line-height: 1.1;
    overflow-wrap: break-word;
  }

  /* The original measured the sender's width and offset the text by it plus the line spacing. */
  .chat__sender {
    margin-right: 2px;
    white-space: nowrap;
    unicode-bidi: isolate;
  }
</style>
